import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './Admin.css';

/**
 * บัญชีดำ (Blacklist CID) — กันเลขบัตรที่ระบุไม่ให้เข้าสู่ระบบ/สมัคร แม้ยังไม่มีบัญชี
 * เห็นและจัดการได้เฉพาะผู้ที่ได้รับสิทธิ์ banned_cid.manage รายคนเท่านั้น
 */
function AdminBannedCids() {
    const { user, loading: authLoading } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [newCid, setNewCid] = useState('');
    const [newReason, setNewReason] = useState('');
    const [saving, setSaving] = useState(false);

    const fetchItems = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get('/admin/banned-cids');
            setItems(res.data);
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาดในการโหลดข้อมูล' });
        } finally {
            setLoading(false);
        }
    }, []);

    // เช็คจาก permission ที่ได้รับจริง (grant รายคน) — จงใจไม่ใช้ hasPermission()
    // เพราะ hasPermission bypass ให้ role admin ทุกคน แต่หน้านี้ต้องเห็นเฉพาะผู้ถูก grant เท่านั้น
    const canManage = !!user?.permissions?.includes('banned_cid.manage');

    useEffect(() => {
        if (canManage) fetchItems();
    }, [canManage, fetchItems]);

    if (authLoading) return null;
    // ไม่มีสิทธิ์ — ไม่แสดงหน้านี้ (แม้เป็น admin)
    if (!canManage) return <Navigate to="/admin" replace />;

    const handleAdd = async (e) => {
        e.preventDefault();
        const cleanCid = newCid.replace(/\D/g, '');
        if (cleanCid.length !== 13) {
            setMessage({ type: 'error', text: 'กรุณากรอกเลขบัตรประชาชนให้ครบ 13 หลัก' });
            return;
        }
        try {
            setSaving(true);
            await api.post('/admin/banned-cids', { cid: cleanCid, reason: newReason });
            setMessage({ type: 'success', text: `เพิ่ม ${cleanCid} เข้าบัญชีดำแล้ว` });
            setNewCid('');
            setNewReason('');
            fetchItems();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async (item) => {
        if (!window.confirm(`ปลด ${item.cid}${item.employee_name ? ` (${item.employee_name})` : ''} ออกจากบัญชีดำ?`)) return;
        try {
            await api.delete(`/admin/banned-cids/${item.id}`);
            setMessage({ type: 'success', text: `ปลด ${item.cid} ออกจากบัญชีดำแล้ว` });
            fetchItems();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        }
    };

    const formatDate = (d) => {
        if (!d) return '-';
        const date = new Date(d);
        return date.toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="admin-page">
            <div className="admin-header">
                <div>
                    <h1>บัญชีดำ (Blacklist)</h1>
                    <p>เลขบัตรที่ถูกระงับ {items.length} รายการ — ห้ามเข้าสู่ระบบและสมัครใช้งาน</p>
                </div>
            </div>

            <div className="info-box info-box-blue mb-3">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                </svg>
                <div>
                    เลขบัตรในบัญชีดำจะ<strong>เข้าสู่ระบบไม่ได้ สมัครไม่ได้ และตรวจสอบสถานะไม่พบข้อมูล</strong>
                    โดยระบบแจ้งเพียง "บัญชีถูกระงับการใช้งาน" ไม่เปิดเผยสาเหตุ — รายการนี้เห็นเฉพาะผู้ได้รับสิทธิ์เท่านั้น
                </div>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type} mb-3`}>
                    {message.text}
                    <button onClick={() => setMessage({ type: '', text: '' })} style={{ float: 'right', border: 'none', background: 'none', cursor: 'pointer' }}>&times;</button>
                </div>
            )}

            {/* เพิ่มรายการ */}
            <div className="steam-card mb-3">
                <div className="steam-card-body">
                    <form onSubmit={handleAdd} className="filters-row">
                        <div className="filter-group">
                            <input
                                type="text"
                                className="steam-input"
                                placeholder="เลขบัตรประชาชน 13 หลัก"
                                value={newCid}
                                maxLength={13}
                                onChange={(e) => setNewCid(e.target.value.replace(/\D/g, ''))}
                            />
                        </div>
                        <div className="filter-group" style={{ flex: 2 }}>
                            <input
                                type="text"
                                className="steam-input"
                                placeholder="เหตุผล (แสดงเฉพาะในหน้านี้ ไม่แสดงให้ผู้ถูกแบนเห็น)"
                                value={newReason}
                                onChange={(e) => setNewReason(e.target.value)}
                            />
                        </div>
                        <button type="submit" className="steam-btn steam-btn-primary" disabled={saving || newCid.length !== 13}>
                            {saving ? 'กำลังบันทึก...' : 'เพิ่มเข้าบัญชีดำ'}
                        </button>
                    </form>
                </div>
            </div>

            {/* ตารางรายการ */}
            <div className="steam-card">
                <div className="steam-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                    {loading ? (
                        <div className="flex-center" style={{ padding: '40px' }}>
                            <div className="spinner"></div>
                        </div>
                    ) : (
                        <table className="steam-table">
                            <thead>
                                <tr>
                                    <th>เลขบัตรประชาชน</th>
                                    <th>ชื่อ-ตำแหน่ง (จากฐานเงินเดือน)</th>
                                    <th>เหตุผล</th>
                                    <th>ระงับโดย</th>
                                    <th>วันที่</th>
                                    <th style={{ width: '120px' }}>จัดการ</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>ไม่มีรายการในบัญชีดำ</td>
                                    </tr>
                                ) : items.map(item => (
                                    <tr key={item.id}>
                                        <td><span className="cid-display">{item.cid}</span></td>
                                        <td>
                                            {item.employee_name ? (
                                                <div>
                                                    <div className="user-name">{item.employee_name}</div>
                                                    {item.employee_position && (
                                                        <div className="user-code">{item.employee_position}</div>
                                                    )}
                                                </div>
                                            ) : '-'}
                                        </td>
                                        <td>{item.reason || '-'}</td>
                                        <td>{item.banned_by_name || '-'}</td>
                                        <td>{formatDate(item.created_at)}</td>
                                        <td>
                                            <button
                                                className="steam-btn steam-btn-sm steam-btn-danger"
                                                onClick={() => handleRemove(item)}
                                            >
                                                ปลดแบน
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AdminBannedCids;
